import React, { useEffect, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
	Typography,
} from "@mui/material";
import { getAiDrinks } from "../../../../../../../services/Admin/AiDrinksApi";

const AddDrinkDialog = ({ open, onClose, onAdd }) => {
	const [loadingDrinks, setLoadingDrinks] = useState(false);
	const [drinks, setDrinks] = useState([]);

	const [formData, setFormData] = useState({
		drink_id: "",
		quantity: 1,
		price: "",
		note: "",
		status: "pending",
	});

	// جلب قائمة المشروبات
	useEffect(() => {
		if (!open) return;

		const fetchDrinks = async () => {
			setLoadingDrinks(true);
			try {
				const res = await getAiDrinks();
				if (Array.isArray(res?.data)) {
					setDrinks(res.data);
				}
			} catch (err) {
				console.error("فشل تحميل المشروبات:", err);
			} finally {
				setLoadingDrinks(false);
			}
		};

		fetchDrinks();
	}, [open]);

	const handleFieldChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleDrinkSelect = (drinkId) => {
		const selectedDrink = drinks.find((d) => d.id === Number(drinkId));

		setFormData((prev) => ({
			...prev,
			drink_id: drinkId,
			price: selectedDrink?.price || "",
		}));
	};

	const handleAddDrink = () => {
		// التحقق من الحقول المطلوبة
		if (!formData.drink_id || !formData.quantity || formData.price === "") {
			alert("يرجى ملء جميع الحقول المطلوبة");
			return;
		}

		onAdd({
			product_id: "",
			drink_id: formData.drink_id,
			quantity: Number(formData.quantity),
			price: formData.price,
			note: formData.note,
			status: "pending",
		});

		// إعادة تعيين النموذج فوراً بعد الإضافة
		resetForm();
	};

	const resetForm = () => {
		setFormData({
			drink_id: "",
			quantity: 1,
			price: "",
			note: "",
			status: "pending",
		});
	};

	const handleCloseDialog = () => {
		// إعادة تعيين النموذج عند غلق الديالوج
		resetForm();
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleCloseDialog}
			maxWidth="md"
			fullWidth
			dir="rtl"
			PaperProps={{
				sx: {
					width: { xs: "100%", md: 820 },
					maxWidth: "100%",
				},
			}}
		>
			<DialogTitle sx={{ fontWeight: "bold", textAlign: "right" }}>
				إضافة مشروب
			</DialogTitle>

			<DialogContent sx={{ pt: 2, px: { xs: 2, md: 3 }, width: "100%" }}>
				<Grid container spacing={2}>
					{/* اختيار المشروب */}
					<Grid size={12}>
						<Autocomplete
							fullWidth
							sx={{ width: "100%" }}
							options={drinks}
							getOptionLabel={(option) =>
								`${option.name} - ${option.price} جنيه (${option.prepare_time} دقائق)`
							}
							value={
								formData.drink_id
									? drinks.find((d) => d.id === Number(formData.drink_id))
									: null
							}
							onChange={(event, newValue) => {
								if (newValue) {
									handleDrinkSelect(newValue.id);
								} else {
									setFormData((prev) => ({
										...prev,
										drink_id: "",
										price: "",
									}));
								}
							}}
							disabled={loadingDrinks}
							loading={loadingDrinks}
							ListboxProps={{
								sx: {
									direction: "rtl",
								},
							}}
							slotProps={{
								paper: {
									sx: {
										width: "100%",
										minWidth: 460,
										maxWidth: "min(90vw, 720px)",
										direction: "rtl",
									},
								},
								popper: {
									sx: {
										minWidth: 460,
										maxWidth: "min(90vw, 720px)",
										"& .MuiAutocomplete-paper": {
											width: "100%",
											minWidth: 460,
											maxWidth: "min(90vw, 720px)",
										},
									},
								},
							}}
							renderOption={(props, option) => (
								<Box
									component="li"
									sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}
									{...props}
								>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography
											variant="body2"
											sx={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
										>
											{option.name}
										</Typography>
										<Typography
											variant="caption"
											color="textSecondary"
											sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
										>
											{option.price} جنيه - {option.prepare_time} دقائق تحضير
										</Typography>
									</Box>
								</Box>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									label="اختر المشروب أو ابحث"
									fullWidth
									sx={{ width: "100%" }}
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{loadingDrinks ? (
													<CircularProgress color="inherit" size={20} />
												) : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
							noOptionsText="لا توجد مشروبات"
							loadingText="جاري التحميل..."
							isOptionEqualToValue={(option, value) => option.id === value.id}
							filterOptions={(options, { inputValue }) => {
								if (!inputValue) return options;
								return options.filter((option) =>
									option.name.toLowerCase().includes(inputValue.toLowerCase())
								);
							}}
						/>
					</Grid>

					{/* الكمية والسعر */}
					<Grid size={{ xs: 12, sm: 6 }}>
						<TextField
							label="الكمية"
							type="number"
							name="quantity"
							value={formData.quantity}
							onChange={handleFieldChange}
							inputProps={{ min: 1, step: 1 }}
							fullWidth
						/>
					</Grid>

					<Grid size={{ xs: 12, sm: 6 }}>
						<TextField
							label="السعر"
							type="number"
							name="price"
							value={formData.price}
							inputProps={{ min: 0, step: 0.01 }}
							InputProps={{ readOnly: true }}
							helperText="يتم تحديد السعر تلقائياً حسب المشروب"
							fullWidth
						/>
					</Grid>

					{/* الملاحظة */}
					<Grid size={12}>
						<TextField
							label="ملاحظة"
							name="note"
							value={formData.note}
							onChange={handleFieldChange}
							fullWidth
						/>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions sx={{ p: 2 }}>
				<Button onClick={handleCloseDialog} variant="outlined">
					إلغاء
				</Button>
				<Button onClick={handleAddDrink} variant="contained">
					إضافة
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddDrinkDialog;
